import React, { useEffect, useState, useRef } from 'react';
import _, { head } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';

// RightRailNavigator (Enhanced)
// - Hover expand with delay (prevents flicker)
// - Framer Motion animation
// - Dot-style icon rail

export default function RightRailNavigator({ items = [] }) {

  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeTimeout = useRef(null);

  const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;

  useEffect(() => {

    const normalized = _.map(items, (item, key) => {
      const id = `crd-${item?._itemKey}`;
      const el = document.getElementById(id);

      return {
        id,
        title: item?.name || 'Untitled',
        el,
      };
    }).filter(s => s.el);

    setSections(normalized);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-40% 0px -50% 0px',
        threshold: 0.1,
      }
    );

    normalized.forEach((s) => {
      if (s.el) observer.observe(s.el);
    });

    return () => observer.disconnect();
  }, [items]);

  const handleEnter = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setIsOpen(true);
  };

  const handleLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 200); // delay prevents flicker
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    setSelectedId(id)
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!sections.length) return null;


  return (
    <div
      style={styles.container}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* Dot Rail */}
      <div
        style={styles.dots}
        onClick={isTouch ? () => setIsOpen(prev => !prev) : undefined}
      >
        {sections.map((s) => (
          <div
            key={s.id}
            onClick={(e) => {
              e.stopPropagation(); // prevent toggle when clicking dot
              scrollTo(s.id);      // 👈 allow dot navigation (nice upgrade)
            }}
            style={{
              ...styles.dot,
              ...(selectedId === s.id ? styles.activeDot : {}),
            }}
          />
        ))}
      </div>

      {/* Animated Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 200, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={'rail-container'}
          // style={styles.panel}
          >
            {sections.map((s) => (
              <div
                key={s.id}
                onClick={() => isOpen && scrollTo(s.id)}
                className={'rail-item'}
                style={{
                  // ...styles.item,
                  ...(selectedId === s.id ? styles.active : {}),
                }}
              >
                {s.title}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  container: {
    position: 'absolute',
    right: 0,
    top: '100px',
    display: 'flex',
    alignItems: 'flex-start',
    zIndex: 1000,
  },

  dots: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px',
    background: '#eee',
    borderRadius: '8px 0 0 8px',
    cursor: 'pointer',
  },

  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#999',
  },

  activeDot: {
    background: 'black',
    transform: 'scale(1.4)',
  },

  panel: {
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '0 0 0 12px',
    padding: '10px',
    overflowY: 'auto',
    maxHeight: '50vh',
    height: '300px',
  },

  item: {
    padding: '.5em',
    marginBottom: '.2em',
    borderRadius: '8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '.9rem',
  },

  active: {
    background: '#f0f4ff',
    fontWeight: '600',
  },
};
